import { useEffect, useState } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { GOOGLE_CLIENT_ID } from './config'
import { getUser, refreshSession, signOut, subscribeToLogout } from './auth'
import { ToastProvider } from './components/Toast'
import ErrorBoundary from './components/ErrorBoundary'
import Navbar from './components/Navbar'
import Spinner from './components/Spinner'
import Login from './pages/Login'
import Bundles from './pages/Bundles'
import Devices from './pages/Devices'
import DeviceDetail from './pages/DeviceDetail'
import Accounts from './pages/Accounts'
import AccountDetail from './pages/AccountDetail'
import BundleDetail from './pages/BundleDetail'
import Reservations from './pages/Reservations'

const SESSION_MESSAGES = {
  'session-expired': 'Your session expired — please sign in again.',
  'signed-out-elsewhere': 'You were signed out in another tab.',
}

export default function App() {
  const [user, setUser] = useState(null)
  const [booting, setBooting] = useState(true)
  const [sessionReason, setSessionReason] = useState(null)
  const location = useLocation()

  // The access token lives in memory, so a reload always starts with none — but
  // the httpOnly refresh cookie may still be good for weeks. Renew silently
  // before deciding whether to show the login screen; this is what keeps a
  // tester signed in across reloads and past the token's 1-hour expiry.
  useEffect(() => {
    let cancelled = false
    refreshSession().then(ok => {
      if (cancelled) return
      if (ok) setUser(getUser())
      setBooting(false)
    })
    return () => { cancelled = true }
  }, [])

  // Session teardown (401, sign-out here or in another tab) renders the login
  // screen via state instead of reloading the page.
  useEffect(() => subscribeToLogout(reason => {
    setUser(null)
    setSessionReason(reason)
  }), [])

  // Nothing decided yet — painting the login screen here would flash it at a
  // user whose session is about to be renewed.
  if (booting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-base-200">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!user) {
    return (
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <Login
          message={SESSION_MESSAGES[sessionReason]}
          onLogin={u => { setSessionReason(null); setUser(u) }}
        />
      </GoogleOAuthProvider>
    )
  }

  return (
    <ToastProvider>
      <div className="min-h-screen bg-base-100">
        <a
          href="#main"
          className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:top-2 focus:left-2 btn btn-sm btn-primary"
        >
          Skip to content
        </a>
        <Navbar user={user} onLogout={() => signOut()} />
        <main id="main">
          <ErrorBoundary resetKey={location.pathname}>
            <Routes>
              <Route path="/" element={<Navigate to="/devices" replace />} />
              <Route path="/bundles" element={<Bundles />} />
              <Route path="/bundles/:id" element={<BundleDetail />} />
              <Route path="/devices" element={<Devices />} />
              <Route path="/devices/:id" element={<DeviceDetail />} />
              <Route path="/accounts" element={<Accounts />} />
              <Route path="/accounts/:id" element={<AccountDetail />} />
              <Route path="/reservations" element={<Reservations />} />
              <Route path="*" element={<Navigate to="/devices" replace />} />
            </Routes>
          </ErrorBoundary>
        </main>
      </div>
    </ToastProvider>
  )
}
